/**
 * État du jeu — logique pure (§4).
 *
 * Le **format de sauvegarde est multi-îles dès maintenant** (§4, §15), alors qu'il n'y
 * a qu'une seule île. Ajouter l'archipel plus tard imposerait une migration de toutes
 * les sauvegardes existantes ; la structure coûte quelques accolades aujourd'hui et
 * évite une journée de migration douloureuse en V2.
 *
 * Le store est observable et **immuable en surface** : chaque commande produit un
 * nouveau snapshot. Le rendu compare des références plutôt que des champs, et le
 * moindre bug d'état devient reproductible depuis une sauvegarde.
 */

import { BOOSTS, ECONOMY, GEM_SPENDING } from '@/config/balance';
import { BUILDINGS, buildingByType } from '@/config/buildings';
import { decorationBySprite } from '@/config/decorations';
import { isLandmark, landmarkByType } from '@/config/landmarks';
import { tryBuyLandmark } from './landmarkPurchase';
import { ERA_STARTING_COINS, canPrestige, multiplierGain, starsFor } from './prestige';
import type { PrestigeProgress } from './prestige';
import {
  earnedOver,
  gridSideAfter,
  nextCost,
  offlineEarnings,
  nextLandCost,
  productionPerSecond,
  tryPurchase,
} from './economy';
import type { BuildingCounts, OfflineEarnings, PurchaseRefusal } from './economy';
import {
  connectToNetwork,
  distanceToNetwork,
  encirclesNetwork,
  reachableFreeCount,
  pruneRoads,
  roadKey,
  servedCells,
} from './roads';
import type { ConnectOutcome, RoadCell } from './roads';
import {
  canRepairStreak,
  createQuestState,
  createWeeklyState,
  dayNumber,
  repairStreak,
  rerollDaily,
  weekNumber,
} from './quests';
import type { QuestCounters, QuestState, WeeklyState } from './quests';
import { claimableMilestones } from './milestones';
import type { Milestone } from './milestones';

/** Version du format de sauvegarde. Toute rupture impose une migration (§4). */
export const SNAPSHOT_VERSION = 1;

export interface PlacedBuilding {
  readonly id: string;
  /** Type économique, jamais renommé — il est écrit dans la sauvegarde. */
  readonly type: string;
  /** Apparence. Les cosmétiques de V2 ne changeront que ceci (§4). */
  readonly skinId: string;
  readonly col: number;
  readonly row: number;
  /** Palier visuel, 1 à 3 (§7). Chaque palier est un MODÈLE différent, pas un
   *  agrandissement : une maison plus grosse ne raconte rien. */
  readonly level: number;
  /**
   * Quart de tour, 0 à 3.
   *
   * Sans orientation, toute la ville regarde du même côté et la grille se lit comme un
   * tableau au lieu d'un quartier. La valeur est DÉTERMINISTE, dérivée de la position :
   * `core` reste pur et testable, et deux chargements de la même sauvegarde donnent
   * exactement la même ville — ce qu'un tirage au sort ne garantirait pas.
   */
  readonly orientation: number;
  /** Modèle choisi parmi les variantes du type. Déterministe, comme l'orientation. */
  readonly variant: number;
  readonly placedAt: number;
}

/** Monument d'héritage conservé au prestige (§16.4). Structure prête, feature en V1. */
export interface Monument {
  readonly id: string;
  readonly type: string;
  readonly col: number;
  readonly row: number;
  readonly era: number;
}

/**
 * Trace laissée par les visiteurs (§16.1) — une ENTITÉ, pas une dérivée.
 *
 * Première version : les traces étaient recalculées depuis `visitorsReceived` à
 * chaque passe de décor. Benoit a demandé de pouvoir les VENDRE et les DÉPLACER
 * (« motivation de partage ») : une dérivée recalculée ne peut ni disparaître ni
 * bouger — il fallait des entités sauvegardées, comme les bâtiments.
 */
export interface VisitorTrace {
  readonly id: string;
  /** `flowerbed` (jardinière dorée), `planter` (verte), `bench`, `fountain`. */
  readonly kind: string;
  readonly col: number;
  readonly row: number;
  readonly placedAt: number;
  /**
   * Décoration ACHETÉE en gemmes, et non laissée par un visiteur (ROADMAP 2.4).
   *
   * Un seul champ, parce qu'une décoration achetée est en tout point une entité de
   * décor : même rendu, même déplacement, même occupation de case. Lui donner sa propre
   * liste dans le snapshot aurait dupliqué trois mécanismes pour distinguer deux
   * origines.
   *
   * Ce que ce drapeau change, et c'est tout : elle **survit au prestige** — on ne fait
   * pas disparaître ce qui a été payé — et elle **ne se vend pas contre des pièces**,
   * ce qui ouvrirait une conversion gemmes → pièces à taux libre, hors de l'échelle
   * publiée.
   */
  readonly bought?: boolean;
}

/**
 * Décoration payée, possédée et PAS posée — le sac à dos (ROADMAP 2.9c).
 *
 * ══ Pourquoi ce champ existe ═════════════════════════════════════════════════
 *
 * Le modèle de la 0.62.0 laissait les décorations achetées EN PLACE au prestige. Or la
 * grille repart en 10 × 10 : une fontaine payée posée en (11, 9) était simplement
 * écartée — une perte silencieuse d'objet payé, la pire chose que ce jeu puisse faire à
 * un joueur qui a dépensé. Le sac à dos rend la survie **vraie par construction** : ce
 * qui est payé est soit posé, soit dans le sac, et aucun chemin ne l'efface.
 *
 * C'est aussi l'état d'attente de la pose au fantôme (2.9a) : l'achat encaisse et met
 * l'objet au sac, la pose l'en sort — annuler un placement ne perd donc jamais rien.
 */
export interface BackpackItem {
  /** Identifiant d'entité, stable de l'achat à la pose. */
  readonly id: string;
  /** Identifiant de catalogue (`config/decorations.ts`), jamais renommé. */
  readonly decorationId: string;
}

/**
 * Source d'une accélération. Le boost de VISITE n'en fait pas partie : il vit côté
 * serveur (§9, table `boosts`) et se lit à part — voir le champ `boosts` de `CitySnapshot`.
 */
export type BoostSource = 'gems' | 'ad' | 'event';

/**
 * Accélération ponctuelle, datée par son EXPIRATION et non par une durée restante.
 *
 * `until` et non `remainingMs` : un onglet masqué une heure retrouve le bon état en
 * revenant, au lieu d'avoir à rattraper des expirations manquées. Même principe que les
 * feux de circulation et les animations — **le temps est fourni, jamais lu.**
 */
export interface Boost {
  readonly source: BoostSource;
  readonly multiplier: number;
  readonly until: number;
}

export interface IslandState {
  readonly id: string;
  readonly theme: string;
  readonly grid: { readonly cols: number; readonly rows: number };
  readonly buildings: readonly PlacedBuilding[];
  readonly monuments: readonly Monument[];
  /** Traces des visiteurs — vendables, déplaçables, sauvegardées. */
  readonly traces: readonly VisitorTrace[];
  /**
   * Cases DÉFRICHÉES — décor d'ambiance retiré par le joueur (ROADMAP 5.3).
   *
   * ⚠️ Le décor d'ambiance n'est stocké nulle part : il se DÉRIVE d'un bruit sur la
   * position de la case (`syncProps`). Il n'y a donc rien à supprimer — on ne peut que
   * mémoriser ce qu'on ne veut plus voir. C'est l'inverse du modèle habituel, et c'est
   * ce qui rend la liste courte : une ville rasée entière tiendrait en 144 clés, une
   * ville normale en trois ou quatre.
   *
   * Conséquence utile : la liste ne peut pas se désynchroniser du décor, puisque le
   * décor n'existe pas comme donnée. Elle ne fait que le masquer.
   */
  readonly cleared: readonly string[];
  /**
   * Réseau routier, en clés de case `"col:row"`.
   *
   * **Sauvegardé et non recalculé** : le tracé dépend de l'ORDRE des poses, chaque
   * bâtiment se raccordant à ce qui existait avant lui. Une dérivée réorganiserait tout
   * le réseau au moindre changement, et la ville que le joueur a composée se
   * redessinerait sous ses yeux.
   */
  readonly roads: readonly string[];
}

export interface CitySnapshot {
  readonly version: number;
  readonly coins: number;
  readonly gems: number;
  /**
   * Repère de crue du registre `gem_grants` (ROADMAP 0.1) — PAS un compteur de gemmes.
   *
   * Il dit « ce snapshot a déjà absorbé le registre serveur jusqu'à ce niveau ». La
   * différence avec le total serveur est ce qui reste à créditer, ce qui rend la
   * réconciliation idempotente et une sauvegarde effacée entièrement récupérable.
   * Toute la règle vit dans `core/paidGems.ts` — ne pas la réimplémenter ailleurs.
   */
  readonly paidGemsClaimed: number;
  /** Pièces cumulées depuis toujours, prestiges compris. Source des jalons et des ligues. */
  readonly totalProduced: number;
  readonly prestigeLevel: number;
  readonly prestigeMultiplier: number;
  /**
   * Valeur de `totalProduced` au début de l'ère en cours.
   *
   * Sans ce repère, le gain de prestige se calculerait sur toute la partie : le seuil
   * serait franchi d'emblée après le premier prestige, et le joueur pourrait prestiger en
   * boucle sans rien produire. `totalProduced`, lui, ne recule JAMAIS — c'est la source
   * des jalons et des ligues, et le faire redescendre re-créditerait des jalons déjà
   * encaissés.
   *
   * Sauvegardes d'avant le prestige : la valeur par défaut est 0, donc toute leur
   * production compte pour l'ère en cours. C'est exactement ce qu'on veut — elles n'ont
   * jamais prestigé.
   */
  readonly eraStartedAt: number;
  /**
   * Accélérations ponctuelles actives (§14, G§3 A3).
   *
   * C'étaient `rushMultiplier` et `rushUntil` — un emplacement UNIQUE, que la pub
   * récompensée, le boost de visite et l'achat en gemmes se disputaient. Le champ portait
   * sa propre condamnation en commentaire : « le jour où deux boosts se cumulent, c'est
   * ici qu'on ouvrira ». Trois défauts mesurés avant cette ouverture : l'offre payante
   * grisée pendant une pub gratuite, un boost de visite écrasé par un achat, un ×5
   * remplacé par un ×2.
   *
   * Le boost de VISITE n'est pas ici : il est calculé par le serveur (`viral/visit.ts`) et
   * lu séparément. Le recopier dans le snapshot en ferait une valeur que le client peut
   * forger, et deux sources de vérité pour un même nombre.
   */
  readonly boosts: readonly Boost[];
  /**
   * Extensions achetées **dans l'ère en cours**. Remis à zéro par le prestige.
   *
   * C'est ce nombre qui commande la courbe de prix (`baseCost × growth ⁿ`) et le
   * plafond de douze : les deux doivent repartir à neuf à chaque ère, sans quoi la
   * seconde ère commencerait avec un terrain hors de prix et aucune extension possible.
   */
  readonly landPurchases: number;
  /**
   * Extensions achetées **depuis toujours**. Ne recule jamais, prestige compris.
   *
   * ⚠️ Ce champ existe parce qu'un compteur de quête qui recule casse la mesure de
   * progression. Une quête vaut `compteur − référence du jour`, et cette soustraction
   * suppose la monotonie. `landPurchases` était le seul des six compteurs à ne pas la
   * tenir : après un prestige, la quête « acheter un terrain » réclamait soudain
   * *référence + 1* achats, jusqu'à treize, et l'hebdomadaire « trois terrains »
   * devenait inatteignable pour le reste de la semaine.
   *
   * Le défaut ne vivait dans aucune fonction — `prestige` a raison de remettre le prix
   * à zéro, `questProgress` a raison de soustraire. Il vivait **entre elles**. Plutôt
   * que de rattraper la référence au moment du prestige (un décalage à maintenir à
   * chaque nouveau compteur remis à zéro), on rend la monotonie **vraie par
   * construction** : les quêtes lisent un compteur qui, par définition, ne descend pas.
   */
  readonly landPurchasesTotal: number;
  readonly visitorsReceived: number;
  /**
   * Fleurs de visiteurs déjà APPARUES, depuis toujours. Monotone, comme tout
   * compteur (règle 21) : vendre une fleur ne la fait pas repousser — sans ce
   * compteur, la vente serait une pompe à fleurs infinies.
   */
  readonly flowersSpawned: number;
  /** Trophées de palier déjà apparus (`bench`, `fountain`…). Vendus ≠ réapparus. */
  readonly landmarksSpawned: readonly string[];
  /**
   * Jour (numéro de jour local) des avances de production comptées dans `skipUsed`.
   *
   * Deux champs et non un tableau de dates : c'est la forme déjà retenue pour la pub
   * (`adsDay`/`adsWatched`), et un plafond quotidien n'a besoin de retenir qu'un jour. Une
   * forme identique pour un besoin identique évite deux façons de lire la même chose.
   */
  readonly skipDay: number;
  /** Avances de production achetées CE jour-là — plafonnées par `GEM_SPENDING.skip`. */
  readonly skipUsed: number;
  /**
   * Nuit blanche achetée et pas encore consommée (G§3, A4).
   *
   * Un booléen, pas un compteur : l'offre desserre le plafond du **prochain** retour, et
   * empiler trois nuits blanches ne voudrait rien dire — le temps hors-ligne, lui, ne
   * s'empile pas. Consommée par le crédit de retour, jamais par le passage du temps.
   */
  readonly nightPass: boolean;
  /** Jour (numéro de jour local) des visionnages de pub comptés dans `adsWatched`. */
  readonly adsDay: number;
  /** Pubs récompensées vues CE jour-là — plafonné par `REWARDED_AD.dailyCap`. */
  readonly adsWatched: number;
  /**
   * Compteurs CUMULÉS, jamais décrémentés — même en vendant un bâtiment.
   *
   * Les quêtes mesurent la progression par différence avec leur valeur du début de
   * journée. Un compteur qui redescendrait à la vente ferait reculer une quête déjà
   * avancée, et vendre-racheter deviendrait un moyen de la bloquer.
   */
  readonly buildingsPlaced: number;
  readonly upgradesBought: number;
  /** Quêtes du jour et chaîne de connexion. */
  readonly quests: QuestState;
  /**
   * Quêtes de la semaine, état SÉPARÉ du quotidien.
   *
   * Les deux périodes n'ont aucune raison de se remettre à zéro ensemble, et les mêler
   * obligerait chaque lecture à préciser de laquelle elle parle.
   */
  readonly weekly: WeeklyState;
  readonly islands: readonly IslandState[];
  /** Décorations payées et pas posées (2.9c). Rien ne les efface, voir `BackpackItem`. */
  readonly backpack: readonly BackpackItem[];
  readonly claimedMilestones: readonly string[];
  readonly createdAt: number;
  readonly updatedAt: number;
}

export const INITIAL_GRID_SIDE = 10;

export function createSnapshot(now: number): CitySnapshot {
  return {
    version: SNAPSHOT_VERSION,
    coins: ECONOMY.startingCoins,
    gems: 0,
    paidGemsClaimed: 0,
    totalProduced: 0,
    prestigeLevel: 0,
    prestigeMultiplier: 1,
    eraStartedAt: 0,
    boosts: [],
    landPurchases: 0,
    landPurchasesTotal: 0,
    visitorsReceived: 0,
    flowersSpawned: 0,
    landmarksSpawned: [],
    skipDay: dayNumber(now),
    skipUsed: 0,
    nightPass: false,
    adsDay: dayNumber(now),
    adsWatched: 0,
    buildingsPlaced: 0,
    upgradesBought: 0,
    quests: createQuestState(dayNumber(now), {
      buildingsPlaced: 0,
      coinsEarned: 0,
      upgradesBought: 0,
      landPurchases: 0,
      visitorsReceived: 0,
    }),
    weekly: createWeeklyState(weekNumber(dayNumber(now)), {
      buildingsPlaced: 0,
      coinsEarned: 0,
      upgradesBought: 0,
      landPurchases: 0,
      visitorsReceived: 0,
    }),
    islands: [
      {
        id: 'home',
        theme: 'meadow',
        grid: { cols: INITIAL_GRID_SIDE, rows: INITIAL_GRID_SIDE },
        buildings: [],
        monuments: [],
        traces: [],
        cleared: [],
        roads: [],
      },
    ],
    backpack: [],
    claimedMilestones: [],
    createdAt: now,
    updatedAt: now,
  };
}

/* ── Lectures dérivées ─────────────────────────────────────────────────── */

export function activeIsland(snapshot: CitySnapshot): IslandState {
  const island = snapshot.islands[0];
  if (island === undefined) {
    throw new Error('Snapshot invalide : aucune île.');
  }
  return island;
}

/** Nombre d'exemplaires par type, toutes îles confondues. */
export function buildingCounts(snapshot: CitySnapshot): BuildingCounts {
  const counts: Record<string, number> = {};

  for (const island of snapshot.islands) {
    for (const building of island.buildings) {
      counts[building.type] = (counts[building.type] ?? 0) + 1;
    }
  }

  return counts;
}

export function currentProduction(snapshot: CitySnapshot, boostMultiplier = 1): number {
  const base = productionPerSecond(buildingCounts(snapshot), {
    prestigeMultiplier: snapshot.prestigeMultiplier,
    boostMultiplier,
    // Les repères en gemmes valent un multiple du meilleur type DÉBLOQUÉ, et les ères en
    // offrent d’entrée : sans le niveau, un repère posé en début d’ère 3 vaudrait une
    // maison (`config/landmarks.ts`).
    prestigeLevel: snapshot.prestigeLevel,
  });

  /*
    Bonus de palier.

    Sans lui, améliorer un bâtiment ne serait que décoratif — or l'amélioration doit
    être une DÉCISION de jeu : investir sur un bâtiment plutôt qu'en poser un de plus.
    Le bonus s'ajoute à la production de base du type, ce qui laisse intacte la courbe
    de coût de `economy.ts` : les paliers enrichissent l'économie, ils ne la remplacent
    pas.
  */
  let tierBonus = 0;
  for (const island of snapshot.islands) {
    for (const building of island.buildings) {
      const definition = buildingByType(building.type);
      if (definition === undefined) continue;
      const factor = ECONOMY.tierMultipliers[Math.min(2, Math.max(0, building.level - 1))] ?? 1;
      tierBonus += definition.baseProduction * (factor - 1);
    }
  }

  return base + tierBonus * snapshot.prestigeMultiplier * boostMultiplier;
}

/**
 * Ce qu'UN exemplaire rapporte, par seconde, tel que le joueur le voit.
 *
 * ══ Pourquoi ce n'est pas la production de base du catalogue ═════════════════
 *
 * Le catalogue donne la production d'un exemplaire NEUF, à l'ère 0, sans boost. Un joueur
 * d'ère 4 avec un bâtiment au palier 3 ne reconnaîtrait pas sa ville dans ce nombre. On
 * applique donc les mêmes modificateurs que le total : palier, prestige, boosts.
 *
 * ⚠️ Un point de repère ne produit pas « son » nombre — il ajoute une part de ce que la
 * ville produit déjà. On rend donc sa CONTRIBUTION : ce que la ville produit avec lui,
 * moins ce qu'elle produirait sans. Dérivé, jamais recopié : le jour où le plafond change,
 * ce chiffre suit tout seul (règle 15).
 */
export function buildingIncome(
  snapshot: CitySnapshot,
  building: PlacedBuilding,
  boostMultiplier = 1,
): number {
  if (isLandmark(building.type)) {
    const island = activeIsland(snapshot);
    const without: CitySnapshot = {
      ...snapshot,
      islands: snapshot.islands.map((current, index) =>
        index === 0
          ? { ...current, buildings: island.buildings.filter((b) => b.id !== building.id) }
          : current,
      ),
    };
    return (
      currentProduction(snapshot, boostMultiplier) - currentProduction(without, boostMultiplier)
    );
  }

  const definition = buildingByType(building.type);
  if (definition === undefined) return 0;

  const factor = ECONOMY.tierMultipliers[Math.min(2, Math.max(0, building.level - 1))] ?? 1;
  return definition.baseProduction * factor * snapshot.prestigeMultiplier * boostMultiplier;
}

export function isCellFree(island: IslandState, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= island.grid.cols || row >= island.grid.rows) {
    return false;
  }
  return !island.buildings.some((building) => covers(building, col, row));
}

/** Côté de l'emprise d'un bâtiment. 1 si le type est inconnu — jamais 0. */
export function footprintOf(type: string): number {
  return buildingByType(type)?.footprint ?? 1;
}

/** Vrai si `building` occupe la case donnée, emprise comprise. */
export function covers(building: PlacedBuilding, col: number, row: number): boolean {
  const size = footprintOf(building.type);
  return (
    col >= building.col &&
    col < building.col + size &&
    row >= building.row &&
    row < building.row + size
  );
}

/**
 * Toutes les cases qu'occuperait un bâtiment posé en `(col, row)`.
 *
 * L'ancre est le coin le plus proche de l'origine : un bâtiment de 2 × 2 posé en (4,5)
 * occupe (4,5), (5,5), (4,6) et (5,6).
 */
export function footprintCells(
  type: string,
  col: number,
  row: number,
): readonly { col: number; row: number }[] {
  const size = footprintOf(type);
  const cells: { col: number; row: number }[] = [];
  for (let dc = 0; dc < size; dc++) {
    for (let dr = 0; dr < size; dr++) cells.push({ col: col + dc, row: row + dr });
  }
  return cells;
}

/**
 * Le bâtiment tient-il entièrement, sans recouvrir quoi que ce soit ?
 *
 * `ignoreId` sert au déplacement : un bâtiment ne doit pas se déclarer en conflit
 * avec lui-même quand on le repose au même endroit ou à côté.
 */
export function canPlace(
  island: IslandState,
  type: string,
  col: number,
  row: number,
  ignoreId?: string,
): boolean {
  /*
    Une ROUTE occupe sa case au même titre qu'un bâtiment.

    Sans ce contrôle, on bâtissait par-dessus le bitume : le bâtiment recouvrait la
    rue à l'écran, le réseau restait enregistré dessous, et la desserte du quartier
    disparaissait sans que rien ne le signale. C'était aussi une cause du gaspillage de
    terrain — le placement automatique comptait ces cases comme libres tout en les
    ayant déjà payées en bitume.
  */
  const roads = new Set(island.roads);
  const cells = footprintCells(type, col, row);

  /*
    ⚠️ UNE DÉCORATION OCCUPE SA CASE, AU MÊME TITRE QU'UNE RUE.

    Rapport de Benoit (20 août) : « j'ai acheté une déco, j'ai pu poser une maison
    dessus ! » — et, dans la foulée, « je ne peux pas vendre les décos déjà
    existantes ». Ce sont les DEUX FACES DU MÊME DÉFAUT, pas deux bugs : le tap
    résout le bâtiment avant la trace (`game.ts`), donc une décoration recouverte
    devient injoignable — invendable, indéplaçable, et perdue à l'ère suivante si
    elle avait été payée.

    Une trace n'est pas du décor d'ambiance : c'est une ENTITÉ d'état, souvent
    achetée en gemmes. Bâtir dessus revenait à effacer un objet payé sans même le
    dire. Le contrôle vaut donc pour la pose manuelle, la pose automatique
    (`findAutoPlacement` passe par ici) et le déplacement d'un bâtiment.

    Les RUES, elles, ne sont pas bloquées par une trace — leur tracé est calculé, pas
    choisi par le joueur, et un refus « impossible de bâtir ici » à cause d'une fleur
    posée quatre cases plus loin serait incompréhensible. Une trace que le bitume
    rattrape est DÉPLACÉE (`relocateBuriedTraces`), jamais enterrée.
  */
  const decorated = new Set(island.traces.map((trace) => roadKey(trace.col, trace.row)));

  const fits = cells.every((cell) => {
    if (
      cell.col < 0 ||
      cell.row < 0 ||
      cell.col >= island.grid.cols ||
      cell.row >= island.grid.rows
    ) {
      return false;
    }

    if (roads.has(roadKey(cell.col, cell.row))) return false;
    if (decorated.has(roadKey(cell.col, cell.row))) return false;

    return !island.buildings.some(
      (building) => building.id !== ignoreId && covers(building, cell.col, cell.row),
    );
  });

  if (!fits) return false;

  /*
    ⚠️ ON N'EMMURE PAS UNE RUE — l'invariant qui manquait.

    C'est la cause racine de tous les défauts de route consignés à l'audit. Le placement
    automatique récompensait l'adjacence au bitume : les quatre voisins d'une case de rue
    se faisaient bâtir l'un après l'autre, la rue se retrouvait cernée, et plus aucun
    chemin ne pouvait la rejoindre. Le bâtiment suivant créait alors sa propre flaque
    isolée. Quatorze poses suffisaient à obtenir quatre bouts de bitume disjoints au
    milieu d'un pâté de maisons — et pas un mètre de réseau.

    Le contrôle porte sur la COMPOSANTE, pas sur la case : un carrefour a ses quatre
    voisins en chaussée sans être pour autant condamné.
  */
  const occupied = new Set<string>();
  for (const building of island.buildings) {
    if (building.id === ignoreId) continue;
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      occupied.add(roadKey(cell.col, cell.row));
    }
  }
  for (const cell of cells) occupied.add(roadKey(cell.col, cell.row));

  return !encirclesNetwork({ grid: island.grid, roads, occupied, footprint: cells });
}

/**
 * Première case libre en partant du centre, en spirale.
 *
 * C'est le « placement auto » du §7 : on veut une ville qui pousse depuis son cœur,
 * pas une rangée qui s'étire vers un coin.
 */
export function findAutoPlacement(
  island: IslandState,
  type: string,
): { col: number; row: number } | null {
  const centreCol = Math.floor((island.grid.cols - 1) / 2);
  const centreRow = Math.floor((island.grid.rows - 1) / 2);
  const roads = new Set(island.roads);

  /*
    ══ LA NOTE MESURE LE COÛT, ELLE NE L'APPROXIME PLUS ═══════════════════════

    Les versions précédentes empilaient des heuristiques — « bonus si une route touche »,
    « bonus par route adjacente », « bonus par voisin bâti » — dont aucune ne mesurait ce
    qui compte vraiment : **combien de bitume neuf cette pose va coûter**, et où elle
    laisse la ville respirer.

    Pire, le terme `served * 8` récompensait les cases entourées de PLUSIEURS routes,
    c'est-à-dire précisément celles qui bouchent une rue. C'est lui qui a produit le
    défaut mesuré à l'audit : les quatre voisins d'une case de bitume se faisaient bâtir,
    la rue se retrouvait emmurée, et le bâtiment suivant créait sa propre flaque isolée.

    Trois quantités, toutes exactes :

    · **`cost`** — la distance réelle au réseau, contournement du bâti compris, donnée
      par un unique parcours en largeur (`distanceToNetwork`). Une case déjà desservie
      coûte 0 ; une case à trois pas coûtera trois cases de bitume. Ce n'est plus un
      proxy, c'est le nombre.
    · **`neighbours`** — l'adjacence au bâti, qui densifie le pâté de maisons et lui fait
      partager la même rue. Modéré : au-delà, la ville devient un bloc plein.
    · **`distance`** — au centre, faiblement : la ville pousse depuis son cœur (§7) sans
      que ce soit au prix du terrain.

    L'invariant « on n'emmure pas une rue » n'est PAS dans la note : il est dans
    `canPlace`, donc dans le domaine des poses possibles. Une règle qui décide de ce qui
    est légal n'a pas sa place dans un barème — un barème se contourne toujours par un
    autre terme qui pèse plus lourd.
  */
  const occupied = new Set<string>();
  for (const building of island.buildings) {
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      occupied.add(roadKey(cell.col, cell.row));
    }
  }

  // Une seule passe pour toute la grille, au lieu d'un balayage par candidat.
  const cost = distanceToNetwork(island.grid, roads, occupied);

  const score = (col: number, row: number): number => {
    const cells = footprintCells(type, col, row);
    const own = new Set(cells.map((cell) => roadKey(cell.col, cell.row)));

    // Le raccordement partira de la case du pourtour la PLUS PROCHE du réseau : c'est
    // ce minimum qui décide de la longueur du segment à tracer.
    let cheapest = Infinity;
    let neighbours = 0;

    for (const cell of cells) {
      for (const [dc, dr] of [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ]) {
        const nc = cell.col + (dc ?? 0);
        const nr = cell.row + (dr ?? 0);
        const key = roadKey(nc, nr);
        if (own.has(key)) continue;

        if (island.buildings.some((b) => covers(b, nc, nr))) {
          neighbours++;
          continue;
        }

        const reach = cost.get(key);
        if (reach !== undefined) cheapest = Math.min(cheapest, reach);
      }
    }

    /*
      Réseau vide, ou emplacement dont aucun accès ne rejoint la ville : on ne l'écarte
      pas — c'est la première pose de la partie, ou une île encore vierge — mais on ne
      le récompense pas non plus.
    */
    const pavement = Number.isFinite(cheapest) ? cheapest : roads.size === 0 ? 0 : 12;

    // Chaque case de bitume neuve pèse plus lourd que trois voisins gagnés : le terrain
    // gaspillé en rues est le défaut le plus visible d'une ville qui s'étale.
    return -pavement * 20 + neighbours * 6;
  };

  /*
    Notation sur TOUTE la grille, et non anneau par anneau.

    La version précédente retenait le meilleur emplacement du premier anneau contenant
    une place valable : la ville restait donc concentrique et se densifiait au centre
    pendant que la moitié de l'île restait vide. En notant partout, un emplacement bien
    desservi et aéré l'emporte même s'il est plus loin — la ville s'étale.

    La distance au centre n'est pas abandonnée, elle devient une composante de la note
    parmi d'autres : la ville pousse toujours depuis son cœur (§7), mais ne s'y entasse
    plus.
  */
  const candidates: { col: number; row: number; value: number }[] = [];

  for (let col = 0; col < island.grid.cols; col++) {
    for (let row = 0; row < island.grid.rows; row++) {
      // L'emprise entière doit tenir : chercher une case libre ne suffit pas pour
      // un bâtiment de 2 × 2, dont trois cases sur quatre peuvent être occupées.
      if (!canPlace(island, type, col, row)) continue;

      const distance = Math.max(Math.abs(col - centreCol), Math.abs(row - centreRow));
      candidates.push({ col, row, value: score(col, row) - distance * 0.18 });
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.value - a.value);

  /*
    ⚠️ LE MEILLEUR CANDIDAT EST VÉRIFIÉ, PAS SUPPOSÉ — et il n'y a AUCUN repli.

    La note est une heuristique : elle range les emplacements, elle ne prouve rien. Deux
    questions ne se tranchent qu'en traçant réellement le raccordement — le réseau est-il
    atteignable d'ici, et **restera-t-il ouvert une fois la rue posée** ?

    Une première version gardait un repli : à défaut de candidat parfait, elle posait au
    meilleur endroit disponible « plutôt que de refuser un achat déjà payé ». C'était
    l'erreur. Une pose qui scelle le réseau ne coûte pas une gêne, elle coûte **la
    ville** : plus aucun raccordement n'est possible ensuite, et agrandir le terrain n'y
    change rien puisque les nouvelles cases sont hors d'atteinte. Mesuré avec ce repli :
    la partie se figeait à 33 bâtiments sur une grille de 1 156 cases, toutes les poses
    suivantes étant refusées faute d'accès.

    Il n'y a donc pas de repli. On descend tout le classement, et on rend `null` si rien
    ne tient — le joueur reçoit « plus de place », ce qui est la vérité.

    Le coût reste celui d'un seul parcours dans le cas courant : le premier candidat
    convient presque toujours. La descente complète ne se paie que dans une ville au bord
    de l'asphyxie, où elle est justement ce qui trouve la dernière issue.
  */
  for (const candidate of candidates) {
    const { outcome, sealsNetwork } = connectionFor(island, type, candidate.col, candidate.row);
    if (outcome.kind === 'unreachable' || sealsNetwork) continue;
    return { col: candidate.col, row: candidate.row };
  }

  return null;
}

/* ── Commandes ─────────────────────────────────────────────────────────── */

export interface TickResult {
  readonly snapshot: CitySnapshot;
  readonly earned: number;
}

/**
 * Fait avancer la production. Le temps est **fourni**, jamais lu ici : c'est ce qui
 * rend l'état reproductible et testable, et ce qui permet de rejouer une session.
 */
export function tick(
  snapshot: CitySnapshot,
  elapsedMs: number,
  now: number,
  boostMultiplier = 1,
): TickResult {
  const earned = earnedOver(currentProduction(snapshot, boostMultiplier), elapsedMs);

  if (earned <= 0) {
    return { snapshot, earned: 0 };
  }

  return {
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + earned,
      totalProduced: snapshot.totalProduced + earned,
      updatedAt: now,
    },
    earned,
  };
}

export interface PlacementResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  /**
   * `no-access` : aucun chemin ne relie l'emplacement au réseau existant.
   *
   * Ce motif remplace le repli muet qui posait une case de bitume isolée pour « ne
   * jamais laisser un bâtiment sans desserte ». Une case qui ne touche aucune autre
   * n'est pas une desserte, et c'est ce faux-semblant qui fragmentait le réseau. Il ne
   * devrait plus jamais se produire par le chemin joueur, `canPlace` interdisant
   * d'emmurer une rue : s'il apparaît, c'est un défaut à corriger.
   */
  /*
    Les refus d'un repère en gemmes (ROADMAP 2.3) entrent ici plutôt que dans une union
    à part : c'est le MÊME geste pour le joueur — « poser ce truc » — et l'écran qui lit
    ce champ n'a aucune raison d'en connaître deux.
    `too-few-gems` sert déjà aux offres en gemmes ; `already-owned` est propre aux repères,
    qui ne s'achètent qu'une fois par ère.
  */
  readonly refusal?:
    PurchaseRefusal | 'cell-occupied' | 'no-space' | 'no-access' | 'too-few-gems' | 'already-owned';
  readonly building?: PlacedBuilding;
  readonly cost: number;
  /** Cases de route ajoutées, dans l'ordre du tracé — pour l'animer. */
  readonly roadPath?: readonly { readonly col: number; readonly row: number }[];
}

/**
 * Cases d'où un bâtiment peut se raccorder : son pourtour, hors bâti.
 *
 * Extrait parce que la pose ET le déplacement en ont besoin, à l'identique. Le
 * déplacement ne raccordait pas du tout — le bâtiment déplacé se retrouvait enclavé, sa
 * rue d'origine élaguée, et le réseau perdait une composante à chaque geste.
 */
function accessCells(
  cells: readonly { col: number; row: number }[],
  occupied: ReadonlySet<string>,
): readonly RoadCell[] {
  const around: RoadCell[] = [];
  for (const cell of cells) {
    for (const [dc, dr] of [
      [0, -1],
      [1, 0],
      [0, 1],
      [-1, 0],
    ]) {
      const col = cell.col + (dc ?? 0);
      const row = cell.row + (dr ?? 0);
      if (!occupied.has(roadKey(col, row))) around.push({ col, row });
    }
  }
  return around;
}

/** Cases bâties d'une île, `ignoreId` excepté. */
function occupiedCells(island: IslandState, ignoreId?: string): Set<string> {
  const occupied = new Set<string>();
  for (const building of island.buildings) {
    if (building.id === ignoreId) continue;
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      occupied.add(roadKey(cell.col, cell.row));
    }
  }
  return occupied;
}

/**
 * Ce que coûterait une pose : son raccordement, et l'état du réseau après coup.
 *
 * ── Pourquoi cette fonction existe ──────────────────────────────────────────
 *
 * Le placement automatique et la pose calculaient chacun leur version du raccordement —
 * l'un par une note approchée, l'autre par le vrai parcours. `findAutoPlacement` pouvait
 * donc proposer un emplacement que `placeBuilding` refusait aussitôt. Deux calculs pour
 * une même question ne peuvent que diverger : il n'y en a plus qu'un.
 *
 * `sealsNetwork` répond à la seule question que le contrôle local ne peut pas trancher :
 * **le réseau garde-t-il une ouverture une fois la rue tracée ?** C'est elle qui empêche
 * la ville de se murer toute seule.
 */
function connectionFor(
  island: IslandState,
  type: string,
  col: number,
  row: number,
  ignoreId?: string,
): { readonly outcome: ConnectOutcome; readonly sealsNetwork: boolean } {
  const cells = footprintCells(type, col, row);
  // L'occupation AVANT la pose sert de référence : c'est elle qui dit combien de terrain
  // était atteignable, donc combien la pose en retranche.
  const previous = occupiedCells(island, ignoreId);
  const occupied = new Set(previous);
  for (const cell of cells) occupied.add(roadKey(cell.col, cell.row));

  const outcome = connectToNetwork({
    grid: island.grid,
    roads: new Set(island.roads),
    blocked: occupied,
    from: accessCells(cells, occupied),
  });

  if (outcome.kind === 'unreachable') return { outcome, sealsNetwork: true };

  // Le réseau TEL QU'IL SERA : les cases tracées deviennent chaussée, donc cessent
  // d'être du terrain libre.
  const roadsAfter = new Set(island.roads);
  for (const cell of outcome.cells) roadsAfter.add(roadKey(cell.col, cell.row));

  /*
    ⚠️ UNE POSE PEUT CONSOMMER, ELLE NE PEUT PAS MURER.

    On compare le terrain atteignable avant et après. Le bâtiment a le droit d'occuper
    ses propres cases et de bitumer sa rue ; il n'a pas le droit de couper la ville du
    reste de son île. Tout écart au-delà de ce qu'il consomme est du terrain STRANDÉ —
    libre à l'écran, hors d'atteinte à jamais.

    C'est ce défaut exact qui bloquait la partie à 31 bâtiments : le bâti formait un mur
    continu autour du réseau, neuf cents cases restaient libres et aucune n'était
    joignable. Les deux gardes précédentes — « une case libre au contact », puis « au
    moins N cases atteignables » — étaient satisfaites par les poches intérieures.

    ── La tolérance, et pourquoi elle n'est pas zéro ─────────────────────────

    Refuser tout retranchement interdirait de laisser un trou d'une case entre deux
    bâtiments, ce qui arrive à chaque pose et se lirait comme un refus arbitraire. On
    tolère donc ce qu'aucun bâtiment ne pourrait de toute façon occuper : moins que la
    plus grande emprise du catalogue. Le seuil se dérive du catalogue, il ne se règle
    pas — il suivra tout seul le jour où un 3 × 3 entrera au jeu.
  */
  const largest = BUILDINGS.reduce((max, definition) => Math.max(max, definition.footprint), 1);
  const before = reachableFreeCount(island.grid, new Set(island.roads), previous);
  const after = reachableFreeCount(island.grid, roadsAfter, occupied);
  const consumed = cells.length + outcome.cells.length;

  return { outcome, sealsNetwork: before - consumed - after > largest * largest - 1 };
}

/**
 * Achète et pose un bâtiment.
 *
 * Achat et placement sont **une seule opération** : un bâtiment acheté mais non posé
 * serait un état intermédiaire à sauvegarder, à afficher et à annuler. Le §7 décrit un
 * geste continu — on choisit, on pose, c'est acheté.
 *
 * Ordre des refus, assumé : le **prix d'abord**, la case ensuite. Sans pièces, taper
 * ailleurs ne débloque rien — le manque d'argent est la contrainte qui lie, tandis
 * qu'une case occupée se corrige d'un autre geste. Le motif rendu pilote le juice
 * (E§1.3), il doit donc désigner le vrai obstacle.
 */
export function placeBuilding(
  snapshot: CitySnapshot,
  type: string,
  cell: { col: number; row: number } | 'auto',
  now: number,
): PlacementResult {
  /*
    ── DEUX CAISSES, UN SEUL CHEMIN DE POSE ──────────────────────────────────

    Un point de repère (ROADMAP 2.3) se paie en GEMMES et ne s'achète qu'une fois ; tout
    le reste — emprise, raccordement au réseau, identifiant, orientation, variante — est
    rigoureusement identique à un bâtiment ordinaire.

    On change donc la caisse, pas le chemin. Écrire un second `placeLandmark` aurait
    dupliqué soixante lignes dont la règle de raccordement, et c'est exactement le genre
    de duplication qui diverge au premier correctif : le réseau se serait raccordé d'un
    côté et pas de l'autre, sans que rien ne le signale.
  */
  const counts = buildingCounts(snapshot);
  const landmark = landmarkByType(type);

  let cost: number;
  let coinsAfter = snapshot.coins;
  let gemsAfter = snapshot.gems;

  if (landmark === undefined) {
    const purchase = tryPurchase(type, counts, snapshot.coins);
    if (!purchase.ok) {
      return {
        snapshot,
        ok: false,
        ...(purchase.refusal === undefined ? {} : { refusal: purchase.refusal }),
        cost: purchase.cost,
      };
    }
    cost = purchase.cost;
    coinsAfter = purchase.coinsAfter;
  } else {
    const purchase = tryBuyLandmark(type, counts, snapshot.gems);
    if (!purchase.ok) {
      return {
        snapshot,
        ok: false,
        ...(purchase.refusal === undefined ? {} : { refusal: purchase.refusal }),
        cost: purchase.cost,
      };
    }
    cost = purchase.cost;
    gemsAfter = purchase.gemsAfter;
  }

  const purchase = { cost, coinsAfter, gemsAfter };

  const island = activeIsland(snapshot);
  const target = cell === 'auto' ? findAutoPlacement(island, type) : cell;

  if (target === null) {
    return { snapshot, ok: false, refusal: 'no-space', cost: purchase.cost };
  }

  // Toute l'emprise, pas la seule case d'ancrage : c'est ce contrôle qui manquait, et
  // qui laissait une épicerie de 2 × 2 se poser par-dessus des maisons.
  if (!canPlace(island, type, target.col, target.row)) {
    return { snapshot, ok: false, refusal: 'cell-occupied', cost: purchase.cost };
  }

  const definition = buildingByType(type);
  if (definition === undefined) {
    return { snapshot, ok: false, refusal: 'unknown-type', cost: purchase.cost };
  }

  /*
    Un raccordement impossible REFUSE la pose au lieu de poser une flaque.

    C'est le contraire de ce que faisait le repli précédent, et c'est le point : un
    bâtiment sans accès n'est pas un bâtiment « à peu près desservi », c'est un défaut de
    conception de la ville. Le joueur reçoit un motif, pas une tache de bitume.

    Le même calcul que celui du placement automatique — littéralement la même fonction,
    pour qu'une proposition ne puisse pas être refusée par la commande qui l'exécute.
  */
  const { outcome } = connectionFor(island, type, target.col, target.row);

  if (outcome.kind === 'unreachable') {
    return { snapshot, ok: false, refusal: 'no-access', cost: purchase.cost };
  }

  const newRoads = outcome.cells;

  const building: PlacedBuilding = {
    /*
      Suffixe tiré de `buildingsPlaced`, CUMULATIF et jamais décrémenté (§ « compteurs
      cumulés »). Il valait `island.buildings.length`, qui redescend à chaque vente :
      poser, vendre, reposer dans la même milliseconde produisait deux bâtiments vivants
      de même identifiant — et `sellBuilding`, `upgradeBuilding` ou `forget` auraient
      agi sur les deux à la fois.
    */
    id: `${type}-${String(now)}-${String(snapshot.buildingsPlaced)}`,
    type,
    skinId: definition.skinId,
    col: target.col,
    row: target.row,
    level: NEW_BUILDING_LEVEL,
    orientation: orientationFor(target.col, target.row),
    variant: variantFor(type, target.col, target.row),
    placedAt: now,
  };

  return {
    ok: true,
    cost: purchase.cost,
    building,
    // Le tracé est rendu à l'appelant DANS L'ORDRE : c'est lui qui permet d'animer le
    // déroulement du segment vers le réseau au lieu de le faire apparaître d'un coup.
    roadPath: newRoads,
    snapshot: {
      ...snapshot,
      coins: purchase.coinsAfter,
      gems: purchase.gemsAfter,
      buildingsPlaced: snapshot.buildingsPlaced + 1,
      updatedAt: now,
      islands: snapshot.islands.map((current, index) =>
        index === 0
          ? {
              ...current,
              buildings: [...current.buildings, building],
              roads: [...current.roads, ...newRoads.map((cell) => roadKey(cell.col, cell.row))],
            }
          : current,
      ),
    },
  };
}

/**
 * Déplace un bâtiment déjà posé. Gratuit (§7) : la ville doit rester réarrangeable.
 *
 * **La commande valide elle-même, avec exactement la règle du fantôme.** Elle testait
 * la seule case d'ancrage via `isCellFree`, sans `ignoreId` et sans les routes : une
 * épicerie 2 × 2 déplacée d'une case se déclarait en conflit **avec elle-même**. Le
 * fantôme, lui, passe bien `movingId` — il s'affichait donc vert, le son de pose
 * partait, et le bâtiment ne bougeait pas. Deux validations différentes pour un même
 * geste ne peuvent que diverger : il n'en reste qu'une, ici, et l'appelant lit le
 * résultat au lieu de le pré-juger.
 */
export function moveBuilding(
  snapshot: CitySnapshot,
  buildingId: string,
  target: { col: number; row: number },
  now: number,
): BuildingActionResult {
  const island = activeIsland(snapshot);
  const building = island.buildings.find((b) => b.id === buildingId);
  if (building === undefined) return { snapshot, ok: false, refusal: 'not-found' };

  if (!canPlace(island, building.type, target.col, target.row, buildingId)) {
    return { snapshot, ok: false, refusal: 'cell-occupied' };
  }

  /*
    ⚠️ LE DÉPLACEMENT RACCORDE, comme la pose — il ne le faisait pas du tout.

    Mesuré : déplacer un bâtiment vers une case vierge le laissait **sans desserte**, et
    `tidyRoads` élaguait ensuite la rue qui menait à son ancienne position. Le réseau
    passait de une à trois composantes en un seul geste. Le joueur ne voyait qu'un
    bâtiment posé au milieu d'un pré, et une rue qui ne menait plus nulle part.

    C'est exactement le même besoin qu'à la pose, donc exactement le même code : le
    pourtour, le parcours en largeur, le tracé rendu dans l'ordre. Deux implémentations
    d'une même règle finissent toujours par diverger — celle-ci n'existait simplement
    pas.

    L'élagage reste la responsabilité de l'appelant, et il doit venir APRÈS : élaguer
    d'abord couperait la rue qu'on est en train de rejoindre.
  */
  const { outcome } = connectionFor(island, building.type, target.col, target.row, buildingId);

  if (outcome.kind === 'unreachable') {
    return { snapshot, ok: false, refusal: 'no-access' };
  }

  const added = outcome.cells.map((cell) => roadKey(cell.col, cell.row));

  return {
    ok: true,
    roadPath: outcome.cells,
    snapshot: {
      ...snapshot,
      updatedAt: now,
      islands: snapshot.islands.map((current, index) =>
        index === 0
          ? {
              ...current,
              buildings: current.buildings.map((existing) =>
                existing.id === buildingId
                  ? { ...existing, col: target.col, row: target.row }
                  : existing,
              ),
              roads: [...current.roads, ...added],
            }
          : current,
      ),
    },
  };
}

/**
 * Retire une case de rue.
 *
 * ── Pourquoi cette commande manquait, et ce qu'elle coûtait ─────────────────
 *
 * Le joueur pouvait vendre un bâtiment, jamais effacer une rue. Or `canPlace` refuse de
 * bâtir sur du bitume : une case de route mal placée gelait donc son terrain
 * **définitivement**, et l'élagage automatique ne la retirait pas dès lors qu'elle
 * bordait un bâtiment. Sur une île saturée, c'était du terrain perdu sans recours.
 *
 * ── Les deux refus, et pourquoi ils sont dans la commande ───────────────────
 *
 * Retirer une rue peut enclaver un bâtiment ou couper le réseau en deux. La commande
 * juge elle-même, exactement comme `moveBuilding` : l'appelant lit le résultat au lieu
 * de pré-juger — deux validations pour un même geste ont déjà divergé une fois dans ce
 * fichier, et le fantôme s'affichait vert sur une pose refusée.
 */
export interface RoadRemovalResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly refusal?: 'not-a-road' | 'serves-building' | 'splits-network';
}

export function removeRoad(
  snapshot: CitySnapshot,
  col: number,
  row: number,
  now: number,
): RoadRemovalResult {
  const island = activeIsland(snapshot);
  const key = roadKey(col, row);
  if (!island.roads.includes(key)) return { snapshot, ok: false, refusal: 'not-a-road' };

  // Un bâtiment dont c'est la SEULE desserte se retrouverait enclavé.
  const remaining = new Set(island.roads.filter((entry) => entry !== key));
  for (const building of island.buildings) {
    const cells = footprintCells(building.type, building.col, building.row);
    const servedBefore = cells.some((cell) =>
      (
        [
          [0, -1],
          [1, 0],
          [0, 1],
          [-1, 0],
        ] as const
      ).some(([dc, dr]) => island.roads.includes(roadKey(cell.col + dc, cell.row + dr))),
    );
    if (!servedBefore) continue;

    const servedAfter = cells.some((cell) =>
      (
        [
          [0, -1],
          [1, 0],
          [0, 1],
          [-1, 0],
        ] as const
      ).some(([dc, dr]) => remaining.has(roadKey(cell.col + dc, cell.row + dr))),
    );
    if (!servedAfter) return { snapshot, ok: false, refusal: 'serves-building' };
  }

  // Couper le réseau en deux morceaux est un défaut, pas un choix : on refuse.
  if (remaining.size > 0) {
    const first = [...remaining][0];
    const seen = new Set<string>([first ?? '']);
    const queue = [first ?? ''];
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let head = 0; head < queue.length; head++) {
      const current = queue[head];
      if (current === undefined) continue;
      const parts = current.split(':');
      const c = Number(parts[0]);
      const r = Number(parts[1]);
      for (const [dc, dr] of [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ] as const) {
        const next = roadKey(c + dc, r + dr);
        if (remaining.has(next) && !seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    if (seen.size !== remaining.size) {
      return { snapshot, ok: false, refusal: 'splits-network' };
    }
  }

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      updatedAt: now,
      islands: snapshot.islands.map((current, index) =>
        index === 0 ? { ...current, roads: [...remaining] } : current,
      ),
    },
  };
}

/**
 * Inscrit le nombre de visites reçues, tel que le SERVEUR le connaît.
 *
 * Trois propriétés, et chacune répare une façon de se tromper :
 *
 * 1. **Le compteur ne recule jamais.** Une requête qui échoue rend `0` (voir
 *    `viral/visit.ts`), et un `0` écrasant un cumul effacerait des paliers déjà gagnés.
 *    Le maximum des deux valeurs rend l'appel sûr même hors ligne.
 * 2. **Le snapshot est rendu tel quel si rien ne change** — l'appelant peut donc sonder
 *    toutes les deux minutes sans provoquer un rendu ni une sauvegarde inutiles.
 * 3. **`prestige` ne l'efface pas** : les visites appartiennent à la ville publique, pas
 *    à l'ère en cours. Une ville partagée ne perd pas ses visiteurs parce que son
 *    propriétaire a prestigé.
 */
export function recordVisitors(snapshot: CitySnapshot, total: number): CitySnapshot {
  const value = Math.max(snapshot.visitorsReceived, Math.floor(Math.max(0, total)));
  if (value === snapshot.visitorsReceived) return snapshot;
  return { ...snapshot, visitorsReceived: value };
}

export interface MilestoneResult {
  readonly snapshot: CitySnapshot;
  readonly claimed: readonly Milestone[];
  readonly gems: number;
}

/** Crédite les jalons atteints. À appeler après chaque commande qui change l'état. */
export function claimMilestones(snapshot: CitySnapshot, now: number): MilestoneResult {
  const claim = claimableMilestones(
    {
      buildingCounts: buildingCounts(snapshot),
      /*
        Le total, et non le compteur d'ère. Les douze jalons de terrain sont des
        récompenses de première fois, protégées du double paiement par
        `claimedMilestones` — le total ne peut donc rien re-créditer. En revanche, un
        joueur qui prestige après cinq extensions et en rachète une doit obtenir le
        jalon « sixième extension » : il en a bien acheté six. Avec le compteur d'ère,
        il lui en aurait fallu six de plus.
      */
      landPurchases: snapshot.landPurchasesTotal,
      visitorsReceived: snapshot.visitorsReceived,
      totalProduced: snapshot.totalProduced,
      prestigeLevel: snapshot.prestigeLevel,
      /*
        ROADMAP 3.2 — les trois familles ajoutées se lisent dans l'île courante, jamais
        dans un compteur parallèle : le réseau et les décorations SONT l'état, et un
        compteur qui les doublerait divergerait au premier nettoyage de rue.
      */
      roadCells: activeIsland(snapshot).roads.length,
      decorations: activeIsland(snapshot).traces.length,
    },
    new Set(snapshot.claimedMilestones),
  );

  if (claim.milestones.length === 0) {
    return { snapshot, claimed: [], gems: 0 };
  }

  return {
    claimed: claim.milestones,
    gems: claim.gems,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems + claim.gems,
      updatedAt: now,
      claimedMilestones: [
        ...snapshot.claimedMilestones,
        ...claim.milestones.map((milestone) => milestone.id),
      ],
    },
  };
}

/* ── Store observable ──────────────────────────────────────────────────── */

export type StateListener = (snapshot: CitySnapshot) => void;

export interface GameStore {
  getSnapshot(): CitySnapshot;
  setSnapshot(snapshot: CitySnapshot): void;
  subscribe(listener: StateListener): () => void;
}

/**
 * Store minimal. Volontairement sans logique : les commandes sont des fonctions pures
 * au-dessus, ce qui permet de les tester sans store et de rejouer une partie.
 */
export function createGameStore(initial: CitySnapshot): GameStore {
  let snapshot = initial;
  const listeners = new Set<StateListener>();

  return {
    getSnapshot: () => snapshot,
    setSnapshot(next) {
      if (next === snapshot) return;
      snapshot = next;
      for (const listener of listeners) listener(snapshot);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Orientation d'un bâtiment posé en (col, row).
 *
 * Deux nombres premiers pour que le motif ne se répète pas visiblement : avec des
 * facteurs pairs, une rangée sur deux se retrouverait orientée à l'identique et le
 * quadrillage redeviendrait lisible.
 */
export function orientationFor(col: number, row: number): number {
  return (col * 7 + row * 3) % 4;
}

/**
 * Palier visuel d'un bâtiment, déduit du nombre d'exemplaires déjà possédés.
 *
 * La ville évolue donc d'elle-même à mesure qu'on construit : les premiers exemplaires
 * restent modestes, les suivants montent en gamme. C'est une progression VISIBLE sans
 * écran d'amélioration ni monnaie supplémentaire — et les bâtiments déjà posés gardent
 * leur palier, ce qui donne à la ville un aspect de couches successives plutôt que
 * d'alignement uniforme.
 */
/**
 * Variante de modèle d'un bâtiment posé en (col, row).
 *
 * Facteurs premiers différents de ceux de l'orientation : sinon variante et orientation
 * seraient corrélées, et toutes les maisons d'un même modèle regarderaient du même côté.
 */
export function variantFor(type: string, col: number, row: number): number {
  const count = Math.max(1, buildingByType(type)?.variants ?? 1);
  return (col * 13 + row * 11) % count;
}

/**
 * ⚠️ `levelForCount` a été SUPPRIMÉ — tout palier s'achète désormais.
 *
 * Deux systèmes de paliers coexistaient et se contredisaient : celui-ci offrait
 * gratuitement le palier 2 au 10ᵉ exemplaire et le palier 3 au 25ᵉ, pendant que
 * `upgradeBuilding` faisait payer exactement la même chose. Le commentaire de
 * `balance.ts` défendait « l'amélioration est une DÉCISION de jeu » — elle était
 * distribuée d'office à côté, et l'amélioration payante ne servait qu'aux vingt-quatre
 * premiers exemplaires.
 *
 * Ce que la ville perd — cet aspect de couches successives où les premiers bâtiments
 * restent modestes — elle le retrouve par les **variantes**, déjà déterministes,
 * gratuites, et bien plus variées : onze maisons distinctes plutôt que trois paliers.
 *
 * Un bâtiment neuf naît donc toujours au palier 1.
 */
export const NEW_BUILDING_LEVEL = 1;

/* ── Terrain (§6, étape 6) ─────────────────────────────────────────────── */

export interface LandResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly cost: number;
  readonly refusal?: 'too-expensive' | 'max-reached';
}

/**
 * Achète une extension de terrain.
 *
 * La grille grandit **dans l'état de jeu**, pas seulement à l'écran : c'est le snapshot
 * qui fait foi, et le rendu s'y aligne. L'inverse — un renderer qui décide de la taille
 * et un état qui suit — rendrait la sauvegarde incohérente au premier rechargement.
 */
export function purchaseLand(snapshot: CitySnapshot, now: number): LandResult {
  const cost = nextLandCost(snapshot.landPurchases);

  if (snapshot.landPurchases >= ECONOMY.land.maxPurchases) {
    return { snapshot, ok: false, cost, refusal: 'max-reached' };
  }
  if (snapshot.coins < cost) {
    return { snapshot, ok: false, cost, refusal: 'too-expensive' };
  }

  const side = gridSideAfter(snapshot.landPurchases + 1, INITIAL_GRID_SIDE);

  return {
    ok: true,
    cost,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins - cost,
      landPurchases: snapshot.landPurchases + 1,
      // ⚠️ Les DEUX, toujours ensemble. L'un porte le prix, l'autre la progression :
      // n'incrémenter que le premier reproduirait le défaut qu'ils séparent.
      landPurchasesTotal: snapshot.landPurchasesTotal + 1,
      islands: snapshot.islands.map((island, index) =>
        index === 0 ? { ...island, grid: { cols: side, rows: side } } : island,
      ),
      updatedAt: now,
    },
  };
}

/* ── Actions sur un bâtiment posé (§7) ─────────────────────────────────── */

export interface BuildingActionResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  /** `cell-occupied` : la cible est hors grille, bâtie, bitumée, ou emmurerait une rue. */
  readonly refusal?:
    'not-found' | 'too-expensive' | 'max-tier' | 'blocked' | 'cell-occupied' | 'no-access';
  readonly amount?: number;
  /** Cases de route tracées par un déplacement, dans l'ordre — pour les animer. */
  readonly roadPath?: readonly RoadCell[];
}

function withBuildings(
  snapshot: CitySnapshot,
  map: (building: PlacedBuilding) => PlacedBuilding | null,
  now: number,
): CitySnapshot {
  return {
    ...snapshot,
    updatedAt: now,
    islands: snapshot.islands.map((island, index) =>
      index === 0
        ? {
            ...island,
            buildings: island.buildings
              .map(map)
              .filter((building): building is PlacedBuilding => building !== null),
          }
        : island,
    ),
  };
}

/** Prix de revente d'un bâtiment : une fraction de ce qu'il a coûté (§7). */
export function sellValue(snapshot: CitySnapshot, buildingId: string): number {
  const building = activeIsland(snapshot).buildings.find((b) => b.id === buildingId);
  const definition = building === undefined ? undefined : buildingByType(building.type);
  if (building === undefined || definition === undefined) return 0;

  // Prix du DERNIER exemplaire acheté, pas du premier : revendre au prix de base
  // transformerait une ville avancée en machine à perdre.
  const owned = Math.max(1, buildingCounts(snapshot)[building.type] ?? 1);
  return Math.floor(nextCost(definition, owned - 1) * ECONOMY.sellRefund);
}

export function sellBuilding(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): BuildingActionResult {
  const island = activeIsland(snapshot);
  if (!island.buildings.some((b) => b.id === buildingId)) {
    return { snapshot, ok: false, refusal: 'not-found' };
  }

  const amount = sellValue(snapshot, buildingId);
  const next = withBuildings(snapshot, (b) => (b.id === buildingId ? null : b), now);

  return { snapshot: { ...next, coins: next.coins + amount }, ok: true, amount };
}

/** Coût de l'amélioration vers le palier suivant. */
export function upgradeCost(snapshot: CitySnapshot, buildingId: string): number {
  const building = activeIsland(snapshot).buildings.find((b) => b.id === buildingId);
  const definition = building === undefined ? undefined : buildingByType(building.type);
  if (building === undefined || definition === undefined || building.level >= 3) return 0;

  /*
    Le prix COURANT du type, pas son prix de base.

    L'ancienne formule — `baseCost × 12 × palier²` — ne dépendait ni du nombre
    d'exemplaires possédés ni des améliorations déjà achetées. Mesuré à l'audit du
    17 août : améliorer 96 maisons de t1 à t3 coûtait 86 400 pièces et sextuplait leur
    production, quand la 97ᵉ maison coûtait 2,4 × 10⁸ pour +0,5/s. Un écart de l'ordre de
    **10⁶** : le levier le plus rentable du jeu était aussi le moins cher, et il le
    devenait davantage à chaque achat.

    En suivant `nextCost`, l'amélioration reste comparable à l'achat à tout moment de la
    partie — un peu meilleure par pièce dépensée, jamais d'un facteur million. Et quand le
    terrain est plein, elle devient la seule façon de croître : c'est ce qui la rend
    intéressante plutôt qu'obligatoire.
  */
  const owned = Math.max(1, buildingCounts(snapshot)[building.type] ?? 1);
  return Math.ceil(nextCost(definition, owned) * ECONOMY.upgradeCostFactor * building.level);
}

export function upgradeBuilding(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): BuildingActionResult {
  const building = activeIsland(snapshot).buildings.find((b) => b.id === buildingId);
  if (building === undefined) return { snapshot, ok: false, refusal: 'not-found' };
  /*
    ⚠️ UN POINT DE REPÈRE N'A PAS DE PALIERS, et ce n'est pas un détail d'affichage.

    Le pack ne rend qu'un seul modèle par repère : le sprite d'un palier 2 s'appellerait
    ${tick}waterTower-t2.png${tick} et n'existe pas. Le moteur ne trouvait donc plus de texture et
    **le bâtiment disparaissait de la ville** — payé en gemmes, puis évanoui (rapport de
    Benoit, 20 août : « il n'y a rien en amélioration, du coup le repère disparaît »).

    Le refus est posé ICI, dans la commande, et pas seulement dans l'écran qui l'appelle :
    un panneau peut avoir un bouton oublié, une commande non.
  */
  if (isLandmark(building.type)) return { snapshot, ok: false, refusal: 'max-tier' };
  if (building.level >= 3) return { snapshot, ok: false, refusal: 'max-tier' };

  const cost = upgradeCost(snapshot, buildingId);
  if (snapshot.coins < cost) return { snapshot, ok: false, refusal: 'too-expensive', amount: cost };

  const next = withBuildings(
    snapshot,
    (b) => (b.id === buildingId ? { ...b, level: b.level + 1 } : b),
    now,
  );

  return {
    snapshot: { ...next, coins: next.coins - cost, upgradesBought: next.upgradesBought + 1 },
    ok: true,
    amount: cost,
  };
}

/** Fait pivoter un bâtiment d'un quart de tour. Gratuit : c'est de la composition. */
export function rotateBuilding(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): BuildingActionResult {
  const island = activeIsland(snapshot);
  if (!island.buildings.some((b) => b.id === buildingId)) {
    return { snapshot, ok: false, refusal: 'not-found' };
  }

  return {
    snapshot: withBuildings(
      snapshot,
      (b) => (b.id === buildingId ? { ...b, orientation: (b.orientation + 1) % 4 } : b),
      now,
    ),
    ok: true,
  };
}

/**
 * Élague le réseau des rues qui ne desservent plus rien.
 *
 * À appeler après toute disparition ou déplacement de bâtiment : la rue qui menait à un
 * bâtiment vendu reste sinon en place, occupe du terrain constructible et donne à la
 * ville cet aspect de rues serpentant dans le vide.
 */
export function tidyRoads(snapshot: CitySnapshot): CitySnapshot {
  const island = activeIsland(snapshot);
  if (island.roads.length === 0) return snapshot;

  const served = servedCells(
    island.buildings.map((building) => ({
      cells: footprintCells(building.type, building.col, building.row),
    })),
  );

  const pruned = pruneRoads(island.roads, served);
  if (pruned.length === island.roads.length) return snapshot;

  return {
    ...snapshot,
    islands: snapshot.islands.map((current, index) =>
      index === 0 ? { ...current, roads: pruned } : current,
    ),
  };
}

/* ── Prestige et gemmes (§6, §14) ──────────────────────────────────────── */

/** Ce que le prestige observe, extrait du snapshot. */
export function prestigeProgress(snapshot: CitySnapshot): PrestigeProgress {
  return { totalProduced: snapshot.totalProduced, eraStartedAt: snapshot.eraStartedAt };
}

export interface PrestigeResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly stars: number;
  readonly gain: number;
  readonly refusal?: 'not-yet';
}

/**
 * Ouvre une nouvelle ère.
 *
 * ── Ce qui survit, et pourquoi chaque cas est un choix ──────────────────────
 *
 * · **Gemmes** : elles se gagnent par jalons et par quêtes, pas par production. Les
 *   perdre punirait un effort sans rapport avec l'ère qu'on referme.
 * · **Jalons encaissés** : les re-créditer ferait du prestige une machine à gemmes.
 * · **Compteurs cumulés** (`buildingsPlaced`, `upgradesBought`) : les quêtes mesurent
 *   par différence avec leur valeur du début de journée. Les remettre à zéro ferait
 *   RECULER une quête déjà avancée.
 * · **`totalProduced`** : source des jalons et des ligues, il ne recule jamais. C'est
 *   `eraStartedAt` qui bouge — le repère, pas le compteur.
 * · **Monuments d'héritage** (§16.4) : c'est leur raison d'être, ils traversent les ères.
 *
 * Repartent à zéro : pièces, bâtiments, routes, terrain. C'est-à-dire tout ce que le
 * multiplicateur permet de reconstruire plus vite.
 */
export function prestige(snapshot: CitySnapshot, now: number): PrestigeResult {
  const progress = prestigeProgress(snapshot);
  const stars = starsFor(progress);

  if (!canPrestige(progress)) {
    return { snapshot, ok: false, stars, gain: 0, refusal: 'not-yet' };
  }

  const gain = multiplierGain(stars);

  /*
    ══ LES DÉCORATIONS PAYÉES VONT AU SAC À DOS (ROADMAP 2.9c) ═══════════════════

    Le modèle précédent les laissait EN PLACE — et écartait celles posées hors de la
    grille réinitialisée : une fontaine payée en (11, 9) était perdue en silence.
    Toutes partent donc au sac, d'où le joueur les repose où il veut sur sa ville
    neuve. La survie ne dépend plus d'aucune coordonnée : elle est vraie par
    construction, quel que soit le terrain de l'ère suivante.

    Une sorte qui ne correspondrait à aucune entrée de catalogue (sauvegarde éditée)
    ne peut pas entrer au sac — elle reste posée si elle tient dans la grille neuve,
    comme un monument : on ne détruit jamais ce qui a été payé.
  */
  const boughtTraces = snapshot.islands[0]?.traces.filter((trace) => trace.bought === true) ?? [];
  const stashed: BackpackItem[] = [];
  const unmappable: VisitorTrace[] = [];
  for (const trace of boughtTraces) {
    const definition = decorationBySprite(trace.kind);
    if (definition === undefined) unmappable.push(trace);
    else stashed.push({ id: trace.id, decorationId: definition.id });
  }

  return {
    ok: true,
    stars,
    gain,
    snapshot: {
      ...snapshot,
      coins: ERA_STARTING_COINS,
      prestigeLevel: snapshot.prestigeLevel + 1,
      prestigeMultiplier: snapshot.prestigeMultiplier + gain,
      // Le repère de l'ère, pas le compteur : `totalProduced` ne recule jamais.
      eraStartedAt: snapshot.totalProduced,
      // Seul le compteur d'ÈRE repart à zéro : c'est lui qui porte le prix et le
      // plafond. `landPurchasesTotal` est délibérément absent de cette liste — le
      // laisser reculer casserait les quêtes de terrain pour le reste de la journée.
      landPurchases: 0,
      // Aucune accélération ne survit à la remise à zéro : elle s'appliquerait à une
      // ville vide, donc à rien. Toutes les sources partent, pas seulement les gemmes.
      boosts: [],
      /*
        Les traces des visiteurs REPOUSSENT dans la nouvelle ère : les compteurs
        d'apparition repartent à zéro avec la ville. Les visiteurs, eux, restent
        acquis (`visitorsReceived` ne recule jamais) — leurs souvenirs renaissent donc
        aussitôt, placés pour la grille neuve. Un trophée qui disparaîtrait au
        prestige serait une perte muette, l'exact contraire du §16.1.

        Les décorations ACHETÉES, elles, partent au SAC À DOS (`stashed`, plus haut) :
        elles ne dépendent d'aucun compteur, rien ne les recréerait — le joueur les
        repose lui-même sur sa ville neuve.
      */
      flowersSpawned: 0,
      landmarksSpawned: [],
      backpack: [...snapshot.backpack, ...stashed],
      updatedAt: now,
      islands: snapshot.islands.map((island, index) =>
        index === 0
          ? {
              ...island,
              grid: { cols: INITIAL_GRID_SIDE, rows: INITIAL_GRID_SIDE },
              buildings: [],
              roads: [],
              /*
                LE DÉFRICHAGE REPOUSSE (5.3). Il est payé en pièces, et les pièces sont
                remises à zéro : le garder en ferait un avantage permanent acheté avec une
                monnaie éphémère, ce qui n'a de sens ni dans un sens ni dans l'autre.

                Et surtout la grille rétrécit à 10 × 10 : la moitié des clés mémorisées
                désigneraient des cases qui n'existent plus. Une liste qui survit à ce
                qu'elle décrit est une fuite lente — c'est déjà ce qui a été corrigé pour
                les décorations posées hors de la grille neuve, deux lignes plus bas.

                Le prix repart donc de sa base : la ville neuve se recompose vite.
              */
              cleared: [],
              // Ne restent posées que les décorations payées qu'aucune entrée de
              // catalogue ne sait ranger au sac — et qui tiennent dans la grille
              // neuve, même règle que les monuments juste en dessous.
              traces: unmappable.filter(
                (trace) => trace.col < INITIAL_GRID_SIDE && trace.row < INITIAL_GRID_SIDE,
              ),
              // Les monuments d'héritage traversent les ères (§16.4). Ceux qui
              // tomberaient hors de la grille réinitialisée sont écartés plutôt que
              // rendus invisibles et bloquants.
              monuments: island.monuments.filter(
                (monument) => monument.col < INITIAL_GRID_SIDE && monument.row < INITIAL_GRID_SIDE,
              ),
            }
          : island,
      ),
    },
  };
}

/* ── Accélérations (G§3) ───────────────────────────────────────────────────
   Une liste, un produit borné, une purge à chaque lecture. Les trois vont
   ensemble : sans la purge la liste grossit sans fin, sans la borne trois
   sources qui se multiplient décalent l'économie d'un ordre de grandeur. */

/** Boosts encore actifs à un instant donné. Les expirés ne sont pas rendus. */
export function activeBoosts(snapshot: CitySnapshot, now: number): readonly Boost[] {
  return snapshot.boosts.filter((boost) => boost.until > now && boost.multiplier > 1);
}

/**
 * Multiplicateur d'accélération à un instant donné — le PRODUIT des boosts actifs.
 *
 * Dérivé du temps plutôt que d'une minuterie : un onglet masqué une heure retrouve le bon
 * état en revenant, au lieu de devoir rattraper des expirations manquées.
 *
 * Le produit et non le maximum : deux sources récompensent deux efforts différents
 * (payer, regarder une pub, être visité), et n'en retenir qu'une annulerait
 * silencieusement l'autre. Borné par `BOOSTS.maxProduct`.
 *
 * Filtrer par source sert au HUD, qui affiche une pastille PAR source avec sa propre
 * échéance : un seul chiffre ne pourrait porter qu'un seul compte à rebours.
 */
export function boostMultiplier(snapshot: CitySnapshot, now: number, source?: BoostSource): number {
  let product = 1;
  for (const boost of activeBoosts(snapshot, now)) {
    if (source !== undefined && boost.source !== source) continue;
    product *= boost.multiplier;
  }
  return Math.min(BOOSTS.maxProduct, Math.max(1, product));
}

/**
 * Accorde une accélération, quelle qu'en soit la source. **Écrivain unique** de `boosts`.
 *
 * Un boost de la même source ne s'empile pas sur lui-même : il PROLONGE, et retient le
 * meilleur multiplicateur. C'est ce qui répare le défaut le plus coûteux de l'ancien
 * emplacement unique — racheter la même offre était refusé, donc le joueur qui voulait
 * dépenser se voyait dire non, alors que prolonger est précisément ce qu'il croyait
 * acheter. Deux sources différentes, elles, se cumulent.
 */
export function grantBoost(
  snapshot: CitySnapshot,
  source: BoostSource,
  multiplier: number,
  durationMs: number,
  now: number,
): CitySnapshot {
  const kept = activeBoosts(snapshot, now);
  const existing = kept.find((boost) => boost.source === source);

  const merged: Boost = {
    source,
    multiplier: Math.max(multiplier, existing?.multiplier ?? 1),
    // Prolongation depuis l'échéance en cours, pas depuis maintenant : sinon racheter une
    // accélération à cinq minutes de sa fin en RETRANCHERAIT.
    until: Math.max(existing?.until ?? now, now) + durationMs,
  };

  return {
    ...snapshot,
    boosts: [...kept.filter((boost) => boost.source !== source), merged].slice(-BOOSTS.maxEntries),
    updatedAt: now,
  };
}

export interface GemPurchaseResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly refusal?: 'too-few-gems' | 'max-reached' | 'already-active' | 'unknown-offer';
}

/**
 * Achète une accélération ponctuelle de la production (§14).
 *
 * **Ponctuelle, jamais permanente.** Le §14 interdit le pay-to-win sur la production ; un
 * coup de pouce que le temps de jeu rattrape ne l'est pas, un multiplicateur définitif le
 * serait. C'est la frontière, et elle passe exactement ici.
 *
 * Trois paliers depuis la 0.51.0 (G§3, A2), et le palier est NOMMÉ, jamais un index : un
 * index se décale à la première insertion dans le catalogue, et la sauvegarde comme la
 * sonde désigneraient alors une autre offre que celle voulue (règle 22).
 */
export function buyRush(snapshot: CitySnapshot, tierId: string, now: number): GemPurchaseResult {
  const tier = GEM_SPENDING.rush.find((entry) => entry.id === tierId);
  if (tier === undefined) return { snapshot, ok: false, refusal: 'unknown-offer' };
  if (snapshot.gems < tier.cost) return { snapshot, ok: false, refusal: 'too-few-gems' };

  return {
    ok: true,
    snapshot: {
      ...grantBoost(snapshot, 'gems', tier.multiplier, tier.durationMs, now),
      gems: snapshot.gems - tier.cost,
    },
  };
}

/** Avances de production déjà achetées aujourd'hui. Lecture seule. */
export function skipsUsedToday(snapshot: CitySnapshot, now: number): number {
  return snapshot.skipDay === dayNumber(now) ? snapshot.skipUsed : 0;
}

export interface SkipResult extends GemPurchaseResult {
  /** Pièces réellement créditées — c'est ce que l'interface annonce et célèbre. */
  readonly coins: number;
}

/**
 * Achète une avance de production — le « coup de pouce » (G§3, A1).
 *
 * ══ Trois règles, et aucune n'est facultative ════════════════════════════════
 *
 * **1. Le montant se calcule HORS accélérations.** `currentProduction(snapshot)` sans
 * multiplicateur de boost : sinon une avance de douze heures achetée pendant un ×5
 * rendrait soixante heures de production, et « accélérer puis avancer » deviendrait une
 * pompe. Le prestige, lui, compte — c'est un acquis permanent du joueur, pas une fenêtre.
 *
 * **2. Elle ne crédite PAS `totalProduced`.** C'est la frontière exacte du §14, et elle
 * est plus fine qu'il n'y paraît : `totalProduced` alimente les jalons (donc des gemmes —
 * une boucle), les étoiles de prestige et le rang de ligue (§15). Créditer ce compteur
 * ferait acheter de la PROGRESSION, pas des pièces. Les gemmes achètent du temps ; elles
 * n'achètent pas un classement.
 *
 * **3. Le plafond quotidien est vérifié ICI**, pas dans l'interface. Un bouton grisé est
 * une politesse ; la règle vit dans la commande.
 */
export function buySkip(snapshot: CitySnapshot, tierId: string, now: number): SkipResult {
  const tier = GEM_SPENDING.skip.tiers.find((entry) => entry.id === tierId);
  if (tier === undefined) return { snapshot, ok: false, refusal: 'unknown-offer', coins: 0 };
  if (snapshot.gems < tier.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems', coins: 0 };
  }

  const used = skipsUsedToday(snapshot, now);
  if (used >= GEM_SPENDING.skip.dailyCap) {
    return { snapshot, ok: false, refusal: 'max-reached', coins: 0 };
  }

  const coins = currentProduction(snapshot) * tier.seconds;

  return {
    ok: true,
    coins,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + coins,
      gems: snapshot.gems - tier.cost,
      skipDay: dayNumber(now),
      skipUsed: used + 1,
      updatedAt: now,
    },
  };
}

/**
 * Achète la nuit blanche : le prochain retour est crédité sur 24 h à 50 % au lieu de 8 h
 * à 25 % (G§3, A4).
 *
 * Refusée si une nuit blanche est déjà en réserve — la seconde ne servirait à rien, et
 * encaisser des gemmes pour rien est exactement ce que faisait l'ancien `buyRush` en
 * refusant un rachat.
 */
export function buyNightPass(snapshot: CitySnapshot, now: number): GemPurchaseResult {
  if (snapshot.nightPass) return { snapshot, ok: false, refusal: 'already-active' };
  if (snapshot.gems < GEM_SPENDING.night.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems' };
  }

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems - GEM_SPENDING.night.cost,
      nightPass: true,
      updatedAt: now,
    },
  };
}

/**
 * Achète une extension de terrain en GEMMES.
 *
 * Prix fixe, là où le prix en pièces croît d'un facteur 3,2 : c'est un raccourci désirable
 * en fin d'ère, jamais un remplacement de l'économie de pièces au début. Le §14 tient —
 * le terrain n'est pas de la production, c'est de la place, et le temps de jeu l'obtient
 * aussi. Le plafond des douze extensions s'applique à l'identique : une monnaie ne rachète
 * pas une règle de conception.
 */
export function buyLandWithGems(snapshot: CitySnapshot, now: number): GemPurchaseResult {
  if (snapshot.landPurchases >= ECONOMY.land.maxPurchases) {
    return { snapshot, ok: false, refusal: 'max-reached' };
  }
  if (snapshot.gems < GEM_SPENDING.land.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems' };
  }

  const side = gridSideAfter(snapshot.landPurchases + 1, INITIAL_GRID_SIDE);

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems - GEM_SPENDING.land.cost,
      landPurchases: snapshot.landPurchases + 1,
      // ⚠️ Les DEUX, toujours ensemble. L'un porte le prix, l'autre la progression :
      // n'incrémenter que le premier reproduirait le défaut qu'ils séparent.
      landPurchasesTotal: snapshot.landPurchasesTotal + 1,
      islands: snapshot.islands.map((island, index) =>
        index === 0 ? { ...island, grid: { cols: side, rows: side } } : island,
      ),
      updatedAt: now,
    },
  };
}

/**
 * Crédite un retour du joueur — le seul écrivain du gain hors-ligne (G§3, A4).
 *
 * ── Pourquoi cette fonction est dans `core` et non dans `game.ts` ────────────
 *
 * Le crédit y vivait sous la forme d'un `creditAway` local, et la décision « quel plafond,
 * quel rendement » aurait dû y vivre aussi. Or c'est une règle d'économie : elle se teste
 * sans navigateur, et elle a maintenant deux cas. Deux appelants (le chargement et le
 * retour d'onglet) partagent donc la même règle au lieu de la recopier — c'est exactement
 * la façon dont deux validations de placement avaient divergé (voir `moveBuilding`).
 *
 * ── La nuit blanche ne se consomme QUE si elle change quelque chose ──────────
 *
 * `awayMs > offlineCapMs` : une absence de vingt minutes n'entame pas la réserve. Sans
 * cette garde, le joueur qui achète sa nuit blanche puis referme le jeu cinq minutes la
 * perdrait pour rien — et rien n'entame la confiance plus vite qu'un achat évaporé.
 *
 * `totalProduced` EST crédité, contrairement à l'avance de production (`buySkip`) : ce
 * temps s'est réellement écoulé, la nuit blanche n'en récupère qu'une part plus grande.
 * L'avance, elle, est du temps acheté — la distinction est toute la frontière du §14.
 */
export interface ReturnCredit {
  readonly snapshot: CitySnapshot;
  readonly earnings: OfflineEarnings;
  /** La nuit blanche a-t-elle été consommée pour ce retour ? L'écran le dit au joueur. */
  readonly nightUsed: boolean;
}

export function creditReturn(snapshot: CitySnapshot, awayMs: number, now: number): ReturnCredit {
  const away = Math.max(0, awayMs);
  const night = snapshot.nightPass && away > ECONOMY.offlineCapMs;

  const earnings = offlineEarnings(
    currentProduction(snapshot),
    away,
    night ? { capMs: GEM_SPENDING.night.capMs, efficiency: GEM_SPENDING.night.efficiency } : {},
  );

  // Rien produit, rien consommé : une ville vide ne doit pas dépenser la réserve.
  if (earnings.coins <= 0) return { snapshot, earnings, nightUsed: false };

  return {
    earnings,
    nightUsed: night,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + earnings.coins,
      // `totalProduced` alimente jalons et ligues : le hors-ligne y compte aussi, sans
      // quoi une nuit d'absence ne ferait avancer aucun objectif.
      totalProduced: snapshot.totalProduced + earnings.coins,
      nightPass: night ? false : snapshot.nightPass,
      updatedAt: now,
    },
  };
}

/**
 * Rachète les heures écrêtées du retour EN COURS (G§3, A4 — second moment d'achat).
 *
 * ── Pourquoi cette commande existe en plus de `buyNightPass` ─────────────────
 *
 * La nuit blanche s'achète avant de fermer le jeu, et `creditReturn` la consomme. Mais le
 * moment où sa valeur est la plus lisible est l'écran de RETOUR : le joueur y lit « 6 h 20
 * n'ont pas été comptées ». Lui proposer là un objet qui n'agira que la prochaine fois,
 * sous un libellé qui parle de ces 6 h 20, serait un mensonge — et un mensonge dans un
 * écran de vente est le plus court chemin vers un remboursement.
 *
 * Le rattrapage est donc RÉTROACTIF, au même prix et pour exactement la même promesse :
 * 24 h comptées à moitié tarif. Le montant n'est pas approché, il est **dérivé** — la
 * différence entre ce que le retour aurait rendu avec la nuit blanche et ce qu'il a
 * réellement rendu. Deux appels à la même fonction, aucune formule à maintenir en double
 * (règle 15 : on dérive ce qui se dérive).
 *
 * ⚠️ `awayMs` est fourni par l'appelant et **doit être celui du retour affiché**. C'est
 * une valeur de session, jamais relue de la sauvegarde : `updatedAt` a déjà été réécrit
 * par le crédit, et le recalculer ici rendrait zéro.
 */
export function recoverReturn(snapshot: CitySnapshot, awayMs: number, now: number): SkipResult {
  if (snapshot.gems < GEM_SPENDING.night.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems', coins: 0 };
  }

  const production = currentProduction(snapshot);
  const plain = offlineEarnings(production, awayMs);
  const generous = offlineEarnings(production, awayMs, {
    capMs: GEM_SPENDING.night.capMs,
    efficiency: GEM_SPENDING.night.efficiency,
  });
  const coins = generous.coins - plain.coins;

  // Rien à rattraper : une absence courte, ou une ville qui ne produit pas. On refuse
  // plutôt que d'encaisser — c'est le même principe que le rachat d'accélération.
  if (coins <= 0) return { snapshot, ok: false, refusal: 'max-reached', coins: 0 };

  return {
    ok: true,
    coins,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins + coins,
      // Ce temps s'est réellement écoulé : il compte pour les jalons et les ligues,
      // contrairement à l'avance de production achetée (`buySkip`).
      totalProduced: snapshot.totalProduced + coins,
      gems: snapshot.gems - GEM_SPENDING.night.cost,
      updatedAt: now,
    },
  };
}

/**
 * Relance les quêtes du jour contre des gemmes (G§3, A6).
 *
 * L'enveloppe est ici et la règle dans `quests.ts` : `core/quests` ne connaît pas les
 * gemmes — elles vivent dans le snapshot — et `gameState` ne connaît pas la sélection des
 * quêtes. Chacun décide de ce qu'il possède.
 */
export function buyQuestReroll(snapshot: CitySnapshot, now: number): GemPurchaseResult {
  if (snapshot.gems < GEM_SPENDING.reroll.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems' };
  }

  const rolled = rerollDaily(snapshot.quests);
  if (!rolled.ok) return { snapshot, ok: false, refusal: 'max-reached' };

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems - GEM_SPENDING.reroll.cost,
      quests: rolled.state,
      updatedAt: now,
    },
  };
}

/** Le rachat de chaîne est-il proposable ? Lecture pure, pour l'écran comme pour la sonde. */
export function canBuyStreakRepair(snapshot: CitySnapshot, now: number): boolean {
  return canRepairStreak(snapshot.quests, weekNumber(dayNumber(now)));
}

/**
 * Rachète la chaîne de connexion rompue (G§6, F1).
 *
 * Le refus est distinct du manque de gemmes : une chaîne intacte, une rupture d'avant-hier
 * et un plafond hebdomadaire atteint sont trois « non » différents, et un bouton grisé
 * muet est exactement ce que l'audit reprochait aux cartes de la boutique.
 */
export function buyStreakRepair(snapshot: CitySnapshot, now: number): GemPurchaseResult {
  if (snapshot.gems < GEM_SPENDING.streakRepair.cost) {
    return { snapshot, ok: false, refusal: 'too-few-gems' };
  }

  const repaired = repairStreak(snapshot.quests, weekNumber(dayNumber(now)));
  if (!repaired.ok) return { snapshot, ok: false, refusal: 'max-reached' };

  return {
    ok: true,
    snapshot: {
      ...snapshot,
      gems: snapshot.gems - GEM_SPENDING.streakRepair.cost,
      quests: repaired.state,
      updatedAt: now,
    },
  };
}

/** Compteurs observés par les quêtes, extraits du snapshot. */
export function questCounters(snapshot: CitySnapshot): QuestCounters {
  return {
    buildingsPlaced: snapshot.buildingsPlaced,
    // `totalProduced` est déjà cumulatif depuis toujours, prestiges compris.
    coinsEarned: snapshot.totalProduced,
    upgradesBought: snapshot.upgradesBought,
    landPurchases: snapshot.landPurchasesTotal,
    visitorsReceived: snapshot.visitorsReceived,
  };
}
